import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Plane, ChevronRight, AlertCircle, Hotel, Zap, Armchair, Luggage, Plus, PlaneLanding, PlaneTakeoff, Radio, ShieldCheck, Gift } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format, parseISO } from 'date-fns';
import { getCityName } from '@/components/airportUtils';
import { getPurchasedBaggageSummary } from '@/utils/baggageAllowance';
import { resolveTripListPrice } from '@/utils/tripListPrice';
import { TRIPS_LIST_ITEM_SHELL } from '@/constants/brandColors';
import { resolveCarrierLogo } from '@/utils/airlineBranding';
import { fmtCurrency } from '@/utils/money';
import { cardStatusText, cardWarning } from '@/utils/flightStatusDisplay';
import { FLIGHT_PREDICTIONS_BRAND } from '@/constants/flightPredictions';
import FlightStatusChip from './FlightStatusChip';
import VipBadge from '@/components/admin/VipBadge';
import { describeTripRewards, tripRewardsOf } from '@/utils/tripRewards';
import { tripHasInsurance, tripInsuranceTotalCents } from '../../../supabase/functions/_shared/trip-insurance.js';

function FlightListItem({ trip, onClick, creatorName, creatorId = null, showPrice = true, showDeferaProductBadge = false, showInsuranceBadge = false, flightStatus = null }) {
    if (!trip || trip.length === 0) return null;

    // Sort flights to find the earliest departure
    const sortedFlights = [...trip].sort((a, b) => new Date(`${a.departure_date}T${a.departure_time || '00:00'}`) - new Date(`${b.departure_date}T${b.departure_time || '00:00'}`));
    const firstFlight = sortedFlights[0];
    
    // Find the primary flight that contains the trip status
    const primaryFlight = trip.find(f => f.is_primary_flight) || firstFlight;
    const tripStatus = primaryFlight.trip_status || firstFlight.status || 'pending';
    const flightData = primaryFlight?.flightData || primaryFlight?.flight_data || {};
    const pm = flightData._payment_method || flightData.payment_method;
    const isDeferaZero = pm === 'defera_zero';
    const isDeferaCapital = pm === 'defera_capital';
    const isPrivateSale = !!flightData.promo_applied;

    // Live seat-map provenance (any flight row in the trip may carry it):
    // `seat_purchase` = bought via the dashboard AFTER booking (shown with a +),
    // `seat_selections_detail` = picked on the live map during checkout.
    const seatSourceData = trip
        .map((f) => f?.flightData || f?.flight_data)
        .find((fd) => fd && (fd.seat_purchase || fd.seat_selections_detail));
    const seatsPurchasedAfterBooking = !!seatSourceData?.seat_purchase;
    const seatsFromLiveMap = seatsPurchasedAfterBooking || !!seatSourceData?.seat_selections_detail;

    const baggageSourceData = trip
        .map((f) => f?.flightData || f?.flight_data)
        .find((fd) => {
            const summary = getPurchasedBaggageSummary(fd);
            return summary && summary.totalCents > 0;
        });
    const purchasedBaggageSummary = baggageSourceData
        ? getPurchasedBaggageSummary(baggageSourceData)
        : null;
    const baggagePurchasedAfterBooking = !!purchasedBaggageSummary;

    // Complimentary rewards the team added (Flight Details → Edit → Rewarded).
    const rewards = tripRewardsOf(trip[0]?.flightData || trip[0]?.flight_data);

    // Admin-only: did the customer buy Faye travel insurance on this trip
    // (at checkout, or added later, in the plan or paid upfront)?
    const hasInsurance = showInsuranceBadge && tripHasInsurance(trip);
    const insuranceCents = hasInsurance ? tripInsuranceTotalCents(trip) : 0;

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-brand-500/10 text-brand-400 border-brand-500/20',
            confirmed: 'bg-green-500/10 text-green-400 border-green-500/20',
            completed: 'bg-sky-400/10 text-sky-300 border-sky-400/20',
            cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
            defaulted: 'bg-orange-600/20 text-orange-400 border-orange-600/30',
            delinquent: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
        };
        return (
            <Badge className={`capitalize rounded-full text-[9px] sm:text-xs font-normal px-2 sm:px-3 py-0.5 sm:py-1 ${styles[status] || styles.pending}`}>
                {status}
            </Badge>
        );
    };

    const getCleanAirlineName = (flight) => {
        const rawName = flight.marketing_airline || flight.airline || 'Unknown';
        
        const majorAirlines = [
            'Delta', 'American', 'United', 'Southwest', 'JetBlue', 'Alaska', 
            'Spirit', 'Frontier', 'Hawaiian', 'Allegiant', 'Sun Country',
            'Virgin Atlantic', 'Virgin America', 'British Airways', 'Lufthansa',
            'Air France', 'KLM', 'Emirates', 'Qatar Airways', 'Singapore Airlines'
        ];
        
        for (const airline of majorAirlines) {
            if (rawName.toLowerCase().includes(airline.toLowerCase())) {
                return airline;
            }
        }
        
        return rawName.split(' ')[0] || 'Unknown';
    };

    // Get airline logo from API data. Name-first via resolveCarrierLogo so an
    // admin airline rename re-brands the logo too.
    const getAirlineLogoFromAPI = (flight) => {
        const flightData = flight.flightData || flight.flight_data || {};
        const slices = flightData.slices || [];
        const firstSlice = slices[0];
        if (firstSlice && firstSlice.segments && firstSlice.segments.length > 0) {
            return resolveCarrierLogo(firstSlice.segments[0].marketing_carrier);
        }
        return null;
    };
    
    // Find a flight with a total_price/totalPrice/total_amount, and calculate full price with service fee AND hold fee
    // CRITICAL: Include hotel price for Flights + Stays packages. Fees are never
    // charged on a Faye premium (src/utils/tripListPrice.js).
    const flightWithPrice = resolveTripListPrice({ trip, firstFlight, isDeferaCapital });

    // Extract stay data for UI display (hotel icon and details)
    const stayDataFromAnyFlight = trip.find(f => f.stay_data || f.stayData);
    const stayData = stayDataFromAnyFlight?.stay_data || stayDataFromAnyFlight?.stayData || null;

    const liveText = flightStatus ? cardStatusText(flightStatus.status) : null;
    // A Defera Predictions early warning, when the flight has one.
    const warning = cardWarning(flightStatus, getCityName);
    const LiveEndIcon = liveText?.end === 'arrival' ? PlaneLanding : PlaneTakeoff;

    const cleanAirlineName = getCleanAirlineName(firstFlight);
    const airlineLogo = getAirlineLogoFromAPI(firstFlight);

    // Check if flight has schedule changes
    const hasFlightChanges = (() => {
        const flightData = firstFlight.flightData || firstFlight.flight_data || {};
        const flightChanges = flightData.flight_changes;
        return flightChanges && flightChanges.slices && flightChanges.slices.length > 0;
    })();

    const getRouteDisplay = () => {
        // Check if custom title is set (admin can override the auto-generated title)
        const customTitle = firstFlight.customTitle || firstFlight.custom_title;
        if (customTitle && customTitle.trim() !== '') {
            return customTitle.trim();
        }
        
        // Otherwise, auto-generate from airport codes
        if (trip.length === 1) {
            return `${getCityName(firstFlight.departure_city)} → ${getCityName(firstFlight.arrival_city)}`;
        } else {
            // For roundtrip, show departure to destination (not back to origin)
            // This assumes the first flight is the outbound leg.
            return `${getCityName(firstFlight.departure_city)} → ${getCityName(firstFlight.arrival_city)}`;
        }
    };

    // Extract customer email and name for admin display
    const customerEmail = firstFlight.created_by;
    // Use the passed creatorName, fallback to parsing the email
    const customerName = creatorName || (customerEmail ? customerEmail.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown');

    return (
        <div
            className="group relative overflow-hidden cursor-pointer"
            onClick={() => onClick && onClick(firstFlight)}
        >
            <div className={`absolute inset-0 ${TRIPS_LIST_ITEM_SHELL} group-hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_4px_24px_-8px_rgba(10,18,51,0.35)]`} />
            <div className="relative p-3 sm:p-5 flex items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-2.5 sm:gap-4 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                        {airlineLogo ? (
                            <img src={airlineLogo} alt={`${cleanAirlineName} logo`} className="w-7 h-7 sm:w-10 sm:h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                            <Plane className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-light text-white truncate text-sm sm:text-lg tracking-tight">{getRouteDisplay()}</p>
                        <div className="flex items-center gap-1.5 sm:gap-2 overflow-hidden mt-0.5">
                            <p className="text-[10px] sm:text-sm text-white/60 font-normal whitespace-nowrap">
                                {firstFlight.departure_date ? format(parseISO(firstFlight.departure_date), 'MMM d') : 'N/A'}
                            </p>
                            {creatorName && (
                                <>
                                    <span className="text-[10px] sm:text-sm text-white/40 flex-shrink-0">•</span>
                                    <p className="text-[10px] sm:text-sm text-white/60 font-normal truncate">
                                        {creatorName}
                                    </p>
                                    {/* Staff-only: VipBadge renders nothing for anyone else. */}
                                    <VipBadge userId={creatorId} />
                                </>
                            )}
                            {(firstFlight.bookingReference || firstFlight.booking_reference || firstFlight.tripReference || firstFlight.trip_reference) && (
                                <>
                                    <span className="text-[10px] sm:text-sm text-white/40 flex-shrink-0">•</span>
                                    <p className={`text-[10px] sm:text-sm font-mono text-white/60 font-normal truncate ${liveText ? 'flex-shrink-0' : ''}`}>
                                        {firstFlight.bookingReference || firstFlight.booking_reference || firstFlight.tripReference || firstFlight.trip_reference}
                                    </p>
                                </>
                            )}
                            {/* Live status: the flight and the time that matters now; its pill sits by the trip status. */}
                            {liveText && (
                                <>
                                    {/* A narrow card has room for the time, not the flight number too. */}
                                    {flightStatus.flight && (
                                        <>
                                            <span className="hidden md:inline text-sm text-white/40 flex-shrink-0">•</span>
                                            <p className="hidden md:block text-sm text-white/60 font-normal whitespace-nowrap flex-shrink-0">{flightStatus.flight}</p>
                                        </>
                                    )}
                                    <span className="text-[10px] sm:text-sm text-white/40 flex-shrink-0">•</span>
                                    {/* On a phone a takeoff or landing plane stands in for "Departs"/"Arrives". */}
                                    <p className="flex min-w-0 items-center gap-1 text-[10px] sm:text-sm text-white/60 font-normal sm:flex-shrink-0">
                                        <LiveEndIcon className="w-3 h-3 flex-shrink-0 sm:hidden" aria-hidden="true" />
                                        <span className="truncate"><span className="sr-only sm:not-sr-only sm:whitespace-nowrap">{liveText.verb} </span>{liveText.time}</span>
                                    </p>
                                    {/* The gate or bags carousel only where the line has room for it. */}
                                    {liveText.extra && (
                                        <>
                                            <span className="hidden lg:inline text-sm text-white/40 flex-shrink-0">•</span>
                                            <p className="hidden lg:block text-sm text-white/60 font-normal truncate">{liveText.extra}</p>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        {warning && (
                            <div
                                className={`flex items-center gap-1.5 mt-1.5 text-[10px] sm:text-xs font-normal ${warning.tone === 'bad' ? 'text-rose-200' : 'text-amber-200'}`}
                                title={FLIGHT_PREDICTIONS_BRAND}
                            >
                                <Radio className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" aria-hidden="true" />
                                <span className="truncate"><span className="sr-only">{FLIGHT_PREDICTIONS_BRAND}: </span>{warning.text}</span>
                            </div>
                        )}
                        {stayData && (
                            <div className="flex items-center gap-1.5 mt-1.5 text-[10px] sm:text-xs text-[hsl(228,35%,58%)]/90 font-normal">
                                <Hotel className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                                <span className="truncate">
                                    {stayData.name} • {stayData.nights} night{stayData.nights !== 1 ? 's' : ''}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                    {hasFlightChanges && (
                        <Badge className="bg-brand-500/10 text-brand-400 border-brand-500/20 text-[9px] sm:text-xs font-normal px-1.5 sm:px-2 py-0.5 sm:py-1 flex items-center gap-0.5 sm:gap-1">
                            <AlertCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            <span className="hidden sm:inline">Flight Changed</span>
                            <span className="sm:hidden">Changed</span>
                        </Badge>
                    )}
                    {isPrivateSale && (
                        <Badge className="bg-brand-500/10 text-brand-300 border-brand-500/20 text-[9px] sm:text-xs font-normal px-1.5 sm:px-2 py-0.5 sm:py-1">
                          Private Sale
                        </Badge>
                    )}
                    {showDeferaProductBadge && (isDeferaZero || isDeferaCapital) && (
                        // DC = premium Royal Deep Blue (matches `--accent-color` family) so the
                        // flagship product reads as "money / banking / capital". DZ keeps the
                        // warm amber/orange treatment because it's the energetic / promo product.
                        // Both pills use the same shape & text size — only the color system differs.
                        //
                        // Status-agnostic: the product (DC vs DZ) is a permanent attribute of the
                        // booking, so admins should see it on every row — including completed
                        // (manually marked or paid-off-then-marked), cancelled, and defaulted.
                        // The earlier `tripStatus === 'confirmed'` gate caused the tag to vanish
                        // the moment ops flipped a paid-off DC trip to "completed" (e.g. GOUWVE),
                        // making the row look like a generic installment booking.
                        <span
                            className={`hidden sm:relative sm:inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full overflow-hidden ${
                                isDeferaCapital
                                    ? 'shadow-[0_0_12px_-4px_rgba(15,35,103,0.45)]'
                                    : 'shadow-[0_0_12px_-4px_rgba(234,88,12,0.25)]'
                            }`}
                            title={isDeferaCapital ? 'Defera Capital' : 'Defera Zero'}
                        >
                            {isDeferaCapital ? (
                                <>
                                    <span className="absolute inset-0 bg-gradient-to-r from-[hsl(231,70%,15%)]/70 via-[var(--accent-color)]/65 to-[hsl(235,80%,30%)]/55" />
                                    <span className="absolute inset-0 bg-[linear-gradient(135deg,transparent_0%,rgba(16, 37, 178,0.10)_50%,rgba(15,35,103,0.06)_100%)]" />
                                    <span className="absolute inset-[1px] rounded-full border border-[hsl(228,45%,22%)]/30" />
                                </>
                            ) : (
                                <>
                                    <span className="absolute inset-0 bg-gradient-to-r from-red-800/30 via-orange-600/35 to-orange-500/30" />
                                    <span className="absolute inset-0 bg-[linear-gradient(135deg,transparent_0%,rgba(251,146,60,0.08)_50%,rgba(185,28,28,0.06)_100%)]" />
                                    <span className="absolute inset-[1px] rounded-full border border-red-900/30" />
                                </>
                            )}
                            <span
                                className={`relative flex items-center gap-1 text-[9px] sm:text-xs font-medium tracking-wider ${
                                    isDeferaCapital ? 'text-[hsl(220,92%,92%)]' : 'text-white'
                                }`}
                            >
                                {!isDeferaCapital && (
                                    <Zap className="w-2.5 h-2.5 sm:w-3 sm:h-3 drop-shadow-[0_0_2px_rgba(251,146,60,0.5)]" />
                                )}
                                {isDeferaCapital ? 'DC' : 'DZ'}
                            </span>
                        </span>
                    )}
                    {hasInsurance && (
                        // Shown on phones too: unlike the seat and bag markers, this
                        // one is money ops still has to act on (the policy gets bought).
                        <span
                            className="inline-flex items-center px-1 py-0.5 sm:px-1.5 sm:py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300"
                            title={insuranceCents > 0 ? `Travel insurance purchased · $${fmtCurrency(insuranceCents)}` : 'Travel insurance purchased'}
                            aria-label="Travel insurance purchased"
                            role="img"
                        >
                            <ShieldCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5" aria-hidden="true" />
                        </span>
                    )}
                    {seatsFromLiveMap && (
                        // Seat-map provenance marker: armchair = seats picked on the
                        // live map during the original booking; armchair+ = seats
                        // purchased from the dashboard AFTER the flight was booked.
                        <span
                            className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-1 rounded-full bg-brand-500/10 border border-brand-400/25 text-brand-300"
                            title={seatsPurchasedAfterBooking
                                ? 'Seats purchased via live seat map after booking'
                                : 'Seats selected via live seat map during booking'}
                        >
                            <Armchair className="w-3 h-3" />
                            {seatsPurchasedAfterBooking && <Plus className="w-2.5 h-2.5 -ml-0.5" strokeWidth={3} />}
                        </span>
                    )}
                    {rewards.length > 0 && (
                        // Complimentary rewards the team added (Edit → Rewarded).
                        <span
                            className="inline-flex items-center px-1 py-0.5 sm:px-1.5 sm:py-1 rounded-full bg-[#e3c27e]/10 border border-[#e3c27e]/30 text-[#ecd39c]"
                            title={`Complimentary: ${describeTripRewards(rewards)}`}
                            aria-label={`Complimentary: ${describeTripRewards(rewards)}`}
                            role="img"
                        >
                            <Gift className="w-3 h-3 sm:w-3.5 sm:h-3.5" aria-hidden="true" />
                        </span>
                    )}
                    {baggagePurchasedAfterBooking && (
                        <span
                            className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-1 rounded-full bg-brand-500/10 border border-brand-400/25 text-brand-300"
                            title={`Checked baggage purchased after booking (${purchasedBaggageSummary.totalBags} bag${purchasedBaggageSummary.totalBags === 1 ? '' : 's'})`}
                        >
                            <Luggage className="w-3 h-3" />
                        </span>
                    )}
                    {/* The flight's live status sits just left of the trip status; on a
                        phone, just above it, so the route keeps its width. */}
                    <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-3">
                        {flightStatus && <FlightStatusChip segment={flightStatus} />}
                        {getStatusBadge(tripStatus)}
                    </div>
                    {showPrice && (
                        <div className="hidden sm:block text-right">
                          <p className="text-sm sm:text-lg font-light text-white tracking-tight">
                            ${(flightWithPrice.grand_total_with_hold || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          {(() => {
                            const fd = firstFlight?.flightData || firstFlight?.flight_data || {};
                            const reserveCashCents = fd._reserve_cash_payment_cents ?? fd.reserve_cash_payment_cents ?? 0;
                            if (reserveCashCents > 0) {
                              return (
                                <p className="text-[10px] text-emerald-400/80 font-normal">
                                  Reserve + ${fmtCurrency(reserveCashCents)} card
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                    )}
                    <ChevronRight className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white/40 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
            </div>
        </div>
    );
}

FlightListItem.propTypes = {
    trip: PropTypes.arrayOf(PropTypes.object),
    onClick: PropTypes.func,
    creatorName: PropTypes.string,
    /** Admins: the trip owner's account id, for the VIP badge. */
    creatorId: PropTypes.string,
    showPrice: PropTypes.bool,
    showDeferaProductBadge: PropTypes.bool,
    /** Admins: mark trips that carry Faye travel insurance. */
    showInsuranceBadge: PropTypes.bool,
    // The flight the card shows live status for (cardSegment in src/utils/flightStatusDisplay.js).
    flightStatus: PropTypes.shape({ flight: PropTypes.string, status: PropTypes.object, forecast: PropTypes.object, connection: PropTypes.object }),
};

export default memo(FlightListItem);
